import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useRecorder — captures microphone audio and exposes live amplitude levels for waveform.
 * Returns the recorded Blob (webm/opus) plus a duration in seconds.
 */
export default function useRecorder() {
    const [isRecording, setIsRecording] = useState(false);
    const [error, setError] = useState(null);
    const [seconds, setSeconds] = useState(0);
    const [levels, setLevels] = useState(new Array(32).fill(4));

    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const rafRef = useRef(null);
    const startedAtRef = useRef(0);
    const intervalRef = useRef(null);

    const cleanup = useCallback(() => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        if (intervalRef.current) clearInterval(intervalRef.current);
        // disconnect()/close() throw if the node is already torn down — that's expected during
        // cleanup, not an error worth surfacing.
        try { analyserRef.current?.disconnect(); } catch (_e) { /* already disconnected */ }
        try { audioCtxRef.current?.close(); } catch (_e) { /* already closed */ }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
        }
        streamRef.current = null;
        analyserRef.current = null;
        audioCtxRef.current = null;
        rafRef.current = null;
        intervalRef.current = null;
    }, []);

    useEffect(() => () => cleanup(), [cleanup]);

    const start = useCallback(async () => {
        setError(null);
        setSeconds(0);
        chunksRef.current = [];
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
                ? "audio/webm;codecs=opus"
                : MediaRecorder.isTypeSupported("audio/webm")
                ? "audio/webm"
                : MediaRecorder.isTypeSupported("audio/mp4")
                ? "audio/mp4"
                : "";

            const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
            mediaRecorderRef.current = mr;

            mr.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };
            mr.start();

            // Audio analyser for waveform
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            const ctx = new AudioCtx();
            audioCtxRef.current = ctx;
            const src = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 64;
            src.connect(analyser);
            analyserRef.current = analyser;

            const data = new Uint8Array(analyser.frequencyBinCount);
            const tick = () => {
                analyser.getByteFrequencyData(data);
                const bars = 32;
                const step = Math.max(1, Math.floor(data.length / bars));
                const next = [];
                for (let i = 0; i < bars; i++) {
                    let sum = 0;
                    for (let j = 0; j < step; j++) sum += data[i * step + j] || 0;
                    const avg = sum / step;
                    next.push(Math.max(4, Math.min(48, (avg / 255) * 48)));
                }
                setLevels(next);
                rafRef.current = requestAnimationFrame(tick);
            };
            tick();

            startedAtRef.current = Date.now();
            intervalRef.current = setInterval(() => {
                setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
            }, 250);

            setIsRecording(true);
        } catch (e) {
            setError(e?.message || "Microphone access denied");
            cleanup();
            throw e;
        }
    }, [cleanup]);

    const stop = useCallback(() => {
        return new Promise((resolve) => {
            const mr = mediaRecorderRef.current;
            if (!mr) return resolve(null);
            const elapsed = Math.max(1, Math.floor((Date.now() - startedAtRef.current) / 1000));
            mr.onstop = () => {
                const type = mr.mimeType || "audio/webm";
                const blob = new Blob(chunksRef.current, { type });
                cleanup();
                setIsRecording(false);
                setLevels(new Array(32).fill(4));
                resolve({ blob, durationSec: elapsed, mimeType: type });
            };
            mr.stop();
        });
    }, [cleanup]);

    const cancel = useCallback(() => {
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== "inactive") {
            // .stop() throws if recorder is already inactive due to a race; safe to swallow.
            try { mr.stop(); } catch (_e) { /* already stopped */ }
        }
        cleanup();
        chunksRef.current = [];
        setIsRecording(false);
        setSeconds(0);
        setLevels(new Array(32).fill(4));
    }, [cleanup]);

    return { isRecording, seconds, levels, error, start, stop, cancel };
}
